import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { limparSessao, msAteRenovar } from './armazem-token';
import { AvisoInatividade } from './AvisoInatividade';
import { chamarApi, ErroApi, renovarUmaVezSo } from './cliente-api';
import { ContextoAuth } from './contexto';
import {
  esquecerSessaoConhecida,
  houveSessaoConhecida,
  jaTentouSilenciosa,
  marcarSessaoConhecida,
} from './deposito-pedido';
import { caminhoAtual, caminhoInternoSeguro, destinoDoUsuario } from './destino';
import {
  concluirAutorizacao,
  enderecoDeSaida,
  FalhaAutenticacao,
  iniciarAutorizacao,
  lerRetorno,
} from './fluxo-autorizacao';
import { criarVigiaDeInatividade, type VigiaDeInatividade } from './inatividade';
import { MENSAGEM_GENERICA, MENSAGEM_SESSAO_ENCERRADA } from './mensagens';
import { type Papel } from './papeis';
import {
  type ContextoAuth as FormaDoContexto,
  type EstadoAuth,
  type EnvelopeLogin,
  type MotivoSaida,
  type UsuarioAutenticado,
} from './tipos';

/**
 * Provedor de autenticacao.
 *
 * Reúne o que docs/AUTENTICACAO-E-ACESSO.md pede do lado da tela:
 *
 * - conclui o fluxo de codigo de autorizacao quando o navegador volta do
 *   Keycloak (secao 2, passos 5 e 6);
 * - carrega usuario, papeis e permissoes do GET /eu, que e a unica fonte de
 *   verdade de acesso (passo 7);
 * - tenta a entrada silenciosa ao recarregar, contra a sessao de identidade do
 *   Keycloak, porque o token vive so em memoria (secao 4);
 * - renova o token antes de vencer, sem o usuario perceber (secao 4);
 * - avisa aos 25 minutos de inatividade e encerra aos 30 (secao 4);
 * - na saida, encerra nos dois lados: API e Keycloak (secao 4).
 */
export function ProvedorAuth({ children }: { children: React.ReactNode }) {
  const navegar = useNavigate();

  const [estado, setEstado] = useState<EstadoAuth>('iniciando');
  const [usuario, setUsuario] = useState<UsuarioAutenticado | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [encerraEm, setEncerraEm] = useState<number | null>(null);

  /**
   * O arranque roda UMA vez. Em desenvolvimento o modo estrito do React monta,
   * desmonta e monta de novo; sem esta trava, a troca do codigo por token
   * aconteceria duas vezes — e o codigo de autorizacao e de uso unico, entao a
   * segunda falharia e apagaria a sessao que a primeira acabou de abrir.
   */
  const arrancou = useRef(false);
  const vigia = useRef<VigiaDeInatividade | null>(null);
  /** Para onde levar depois de entrar, quando a tentativa silenciosa falhou. */
  const destinoPretendido = useRef('');

  const carregarEu = useCallback(
    async (destino: string) => {
      try {
        const envelope = await chamarApi<EnvelopeLogin>('/bff/v1/login/verbs/concluir_retorno', {
          metodo: 'POST',
          corpo: { destino },
        });
        const eu = envelope.state.sessao;
        if (!eu) throw new Error('BFF de login não projetou a sessão reconciliada.');

        setUsuario(eu);
        setAviso(null);
        setEstado('autenticado');
        marcarSessaoConhecida();

        const alvo = caminhoInternoSeguro(destino) || destinoDoUsuario(eu);
        navegar(alvo, { replace: true });
      } catch (erro) {
        const falha = erro instanceof ErroApi ? erro : null;

        /**
         * A URL e limpa nos dois caminhos de falha. Ela pode carregar o codigo de
         * autorizacao, que e de uso unico: deixado ali, recarregar a pagina
         * tentaria trocar de novo um codigo ja gasto, e a pessoa veria uma falha
         * que nao e a de verdade.
         */
        navegar('/login', { replace: true });

        // 403 e o caso de conta autenticada sem direito de entrar: sem papel do
        // TEG, ou sem o segundo fator que o papel exige. Mandar "entre de novo"
        // seria conselho errado — entrar de novo daria no mesmo.
        if (falha?.pedeOutroCaminho) {
          setUsuario(null);
          setAviso(falha.aviso);
          setEstado('sem-acesso');
          return;
        }

        limparSessao();
        esquecerSessaoConhecida();
        setUsuario(null);
        setAviso(falha?.aviso ?? MENSAGEM_GENERICA);
        setEstado('anonimo');
      }
    },
    [navegar],
  );

  useEffect(() => {
    if (arrancou.current) {
      return;
    }
    arrancou.current = true;

    void (async () => {
      const busca = window.location.search;
      const retorno = lerRetorno(busca);

      if (retorno) {
        const eraSilenciosa = jaTentouSilenciosa();
        setEstado('entrando');

        try {
          const concluida = await concluirAutorizacao(busca);
          await carregarEu(concluida.destino);
        } catch (erro) {
          const falha = erro instanceof FalhaAutenticacao ? erro : null;
          console.warn('Entrada não concluída.', falha?.causa ?? erro);

          // Tentativa silenciosa recusada e resposta esperada, nao incidente:
          // significa que nao havia sessao no Keycloak. A pessoa ve a tela de
          // entrada limpa, sem susto.
          esquecerSessaoConhecida();
          setAviso(eraSilenciosa ? null : (falha?.aviso ?? MENSAGEM_GENERICA));
          setEstado('anonimo');
          navegar('/login', { replace: true });
        }
        return;
      }

      /**
       * Recarregou a pagina e havia sessao nesta aba: o token morreu com a
       * memoria, mas a sessao de identidade do Keycloak pode estar viva.
       * `prompt=none` resolve isso sem pedir nada ao usuario. A trava de
       * tentativa evita vaivem quando nao ha sessao mesmo.
       *
       * So dispara com sessao conhecida — nunca so por o endereco nao estar
       * numa lista de rotas publicas. Um endereco que nao existe tambem nao
       * esta nessa lista, e sem essa condicao a pessoa era jogada para o
       * Keycloak antes de ver a tela de "nao encontrada" (RotaProtegida ja
       * cuida do redirecionamento de quem tenta uma rota protegida sem
       * sessao, via navegacao no cliente, sem depender disto).
       */
      if (houveSessaoConhecida() && !jaTentouSilenciosa()) {
        destinoPretendido.current = caminhoAtual();
        await iniciarAutorizacao({ destino: caminhoAtual(), silencioso: true });
        return;
      }

      setEstado('anonimo');
    })();
  }, [carregarEu, navegar]);

  const iniciarPeloBff = useCallback((provedor: 'senha' | 'govbr', destino?: string) => {
    const alvo = caminhoInternoSeguro(destino ?? destinoPretendido.current ?? caminhoAtual());
    void (async () => {
      try {
        const verbo = provedor === 'senha' ? 'autenticar_senha' : 'iniciar_govbr';
        const envelope = await chamarApi<EnvelopeLogin>(`/bff/v1/login/verbs/${verbo}`, {
          publica: true,
          metodo: 'POST',
          corpo: { destino: alvo },
        });
        if (envelope.event.outcome === 'REJECTED') {
          setAviso(envelope.state.erro?.mensagem ?? 'Este provedor não está disponível.');
          setEstado('anonimo');
          return;
        }
        await iniciarAutorizacao({
          destino: alvo,
          dicaProvedor: provedor === 'govbr' ? 'govbr' : undefined,
        });
      } catch (erro) {
        setAviso(erro instanceof ErroApi ? erro.aviso : MENSAGEM_GENERICA);
        setEstado('anonimo');
      }
    })();
  }, []);

  const entrar = useCallback(
    (destino?: string) => iniciarPeloBff('senha', destino),
    [iniciarPeloBff],
  );
  const entrarGovbr = useCallback(
    (destino?: string) => iniciarPeloBff('govbr', destino),
    [iniciarPeloBff],
  );

  const sair = useCallback(async (opcoes?: { todas?: boolean; motivo?: MotivoSaida }) => {
    const motivo = opcoes?.motivo ?? 'usuario';

    // Lado da API primeiro, enquanto o token ainda vale. Falha aqui nao pode
    // impedir a saida: melhor sair sem registrar do que ficar preso dentro.
    try {
      await chamarApi<EnvelopeLogin>('/bff/v1/login/verbs/encerrar_sessao', {
        metodo: 'POST',
        corpo: { todas: Boolean(opcoes?.todas) },
      });
    } catch (erro) {
      console.warn('Não foi possível encerrar a sessão na API.', erro);
    }

    const volta = new URL(`${import.meta.env.BASE_URL}login`, window.location.origin);
    volta.searchParams.set('motivo', motivo === 'usuario' ? 'saida' : motivo);

    // O endereco de saida usa o token de identidade, entao e montado ANTES de
    // limpar a memoria.
    const endereco = enderecoDeSaida(volta.toString());

    vigia.current?.parar();
    setEncerraEm(null);
    limparSessao();
    esquecerSessaoConhecida();
    setUsuario(null);
    setEstado('anonimo');

    window.location.assign(endereco);
  }, []);

  /**
   * "Continuar conectado": renova o token E fala com a API.
   *
   * A conversa com a API e o que importa: a inatividade e medida lá, em
   * `sessoes_usuario.ultima_atividade_em`, e renovar token no Keycloak nao toca
   * nesse relogio. Sem esta chamada, o aviso seria dispensado na tela e a
   * requisicao seguinte cairia com sessao expirada.
   */
  const continuarConectado = useCallback(async () => {
    try {
      await renovarUmaVezSo();
      const envelope = await chamarApi<EnvelopeLogin>('/bff/v1/login/verbs/confirmar_mfa', {
        metodo: 'POST',
      });
      setUsuario(envelope.state.sessao);
      vigia.current?.reiniciar();
      setEncerraEm(null);
    } catch (erro) {
      console.warn('Não foi possível manter a sessão.', erro);
      setEncerraEm(null);
      await sair({ motivo: 'sessao-invalida' });
    }
  }, [sair]);

  /**
   * "Editar meus dados" gravou o nome novo no Keycloak e no espelho. O token em
   * uso ainda traz o antigo, e a API copia o nome do token para o espelho a
   * cada reconciliacao: renovar agora faz o token seguinte ja trazer o novo.
   * Se a renovacao falhar, a agendada abaixo tenta de novo; o nome na tela
   * muda do mesmo jeito, porque ja esta gravado.
   */
  const aplicarNovoNome = useCallback(async (nome: string) => {
    await renovarUmaVezSo().catch(() => false);
    setUsuario((atual) => (atual ? { ...atual, nome } : atual));
  }, []);

  /** Renovacao silenciosa, agendada pouco antes de o token vencer. */
  useEffect(() => {
    if (estado !== 'autenticado') {
      return;
    }

    let cancelado = false;
    let temporizador: ReturnType<typeof setTimeout> | null = null;

    const agendar = () => {
      // Piso de 5 s: token de vida curtissima nao pode virar laco de renovacao.
      temporizador = setTimeout(
        () => {
          void (async () => {
            const renovou = await renovarUmaVezSo();
            if (cancelado) {
              return;
            }
            if (renovou) {
              agendar();
              return;
            }
            // Sem renovacao nao ha sessao. Nao insiste: pede a entrada.
            esquecerSessaoConhecida();
            setUsuario(null);
            setAviso(MENSAGEM_SESSAO_ENCERRADA);
            setEstado('anonimo');
            navegar('/login', { replace: true });
          })();
        },
        Math.max(msAteRenovar(), 5_000),
      );
    };

    agendar();

    return () => {
      cancelado = true;
      if (temporizador) {
        clearTimeout(temporizador);
      }
    };
  }, [estado, navegar]);

  /** Vigia de inatividade: 25 minutos avisa, 30 encerra. */
  useEffect(() => {
    if (estado !== 'autenticado') {
      return;
    }

    const criado = criarVigiaDeInatividade({
      aoAvisar: (quando) => setEncerraEm(quando),
      aoEncerrar: () => {
        setEncerraEm(null);
        void sair({ motivo: 'inatividade' });
      },
      aoRetomar: () => setEncerraEm(null),
    });

    criado.iniciar();
    vigia.current = criado;

    return () => {
      criado.parar();
      vigia.current = null;
    };
  }, [estado, sair]);

  const valor = useMemo<FormaDoContexto>(
    () => ({
      estado,
      usuario,
      aviso,
      entrar,
      entrarGovbr,
      sair,
      continuarConectado,
      aplicarNovoNome,
      temPapel: (...papeis: Papel[]) =>
        Boolean(usuario) && papeis.some((papel) => usuario?.papeis.includes(papel)),
      pode: (...permissoes: string[]) =>
        Boolean(usuario) && permissoes.some((permissao) => usuario?.permissoes.includes(permissao)),
    }),
    [estado, usuario, aviso, entrar, entrarGovbr, sair, continuarConectado, aplicarNovoNome],
  );

  return (
    <ContextoAuth.Provider value={valor}>
      {children}
      {estado === 'autenticado' && encerraEm !== null && (
        <AvisoInatividade
          encerraEm={encerraEm}
          aoContinuar={() => void continuarConectado()}
          aoSair={() => void sair({ motivo: 'usuario' })}
        />
      )}
    </ContextoAuth.Provider>
  );
}
